import React from 'react';
import {
    FlexCenter,
    FlexCol,
    FlexColMd,
    FlexEnd,
    FlexSpaceBetween,
    Headline2,
    ModalBackground,
    TertiaryButton,
} from 'components/Elements';
import { sizes, theme } from 'theme';
import useKeyboardTrap from '../../hooks/useKeyboardTrap';
import styled from 'styled-components';
import CloseIcon from 'assets/icons/controls/CloseIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import strings from 'strings/strings.json';

type ModalProps = {
    onClickCloseButton: () => void;
    headlineText?: string;
    modalBody: JSX.Element;
    big?: boolean;
    headlineElement?: JSX.Element;
};

export default function Modal({
    onClickCloseButton,
    headlineText,
    headlineElement,
    modalBody,
    big = false,
}: ModalProps): JSX.Element {
    const { ref } = useKeyboardTrap(onClickCloseButton, true);

    const [isOpen, setIsOpen] = React.useState(false);
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setIsOpen(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const closeButton = (
        <TertiaryButton aria-label={strings.modal.closeButton} onClick={onClickCloseButton}>
            <CloseIconContainer>
                <CloseIcon size={IconSize.SMALL} />
            </CloseIconContainer>
        </TertiaryButton>
    );

    return (
        <ModalBackground id="modal-background">
            <ModalBody
                ref={ref}
                id="modal-body"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-heading"
                aria-describedby="modal-description"
                data-state={isOpen ? 'open' : 'closed'}
            >
                <ModalCard id="modal-card" $big={big} data-state={isOpen ? 'open' : 'closed'}>
                    <FlexColMd id="modal-content">
                        {headlineText || headlineElement ? (
                            <FlexSpaceBetween>
                                {headlineText && <Headline2>{headlineText}</Headline2>}
                                {headlineElement && headlineElement}
                                {closeButton}
                            </FlexSpaceBetween>
                        ) : (
                            <FlexEnd>{closeButton}</FlexEnd>
                        )}
                        {/* modalBody should contain id="modal-heading" and id="modal-description" for screen reader */}
                        {modalBody}
                    </FlexColMd>
                </ModalCard>
            </ModalBody>
        </ModalBackground>
    );
}

const ModalBody = styled(FlexCenter)`
    align-items: flex-start;
    padding: 8rem ${sizes.spacingMd} 0;

    position: fixed;
    inset: 0;
    overflow-y: auto;

    opacity: 0;
    transition: opacity 240ms ease;
    &[data-state='open'] {
        opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const ModalCard = styled(FlexCol)<{ $big?: boolean }>`
    flex-grow: 1;
    max-width: ${({ $big }) => ($big ? '1400px' : '750px')};
    width: 100%;
    padding: ${sizes.spacingMd};
    background-color: ${theme.cardBackground};
    border-radius: ${sizes.borderRadiusLg};

    max-height: calc(100vh - 100px - ${sizes.spacingMd} * 2);
    overflow-y: auto;

    transform-origin: 50% 50%;
    opacity: 0;
    transform: scale(0.92);
    transition:
        opacity 240ms ease,
        transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1); /* nice ease */

    &[data-state='open'] {
        opacity: 1;
        transform: scale(1);
    }

    /* Respect users that have reduced motion enabled */
    will-change: transform, opacity;
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const CloseIconContainer = styled.div`
    padding: ${sizes.spacingSm};
`;
